import React, { useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { useTranslation } from "react-i18next";
import { meetingMediaUrl } from "../../api/Meetings/meetingSessionsApi";
import {
  extractVideoTranscriptSections,
  mediaKindFromMime,
  notesByPhotoId,
  audioCouplingByPhotoId,
  shortMeetingBlurb,
} from "../../utils/meetingRecorder";

const PRINT_STYLE_ID = "meeting-summary-print-style";

function ensurePrintStyles() {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(PRINT_STYLE_ID);
  if (existing) existing.remove();
  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = `
@media screen {
  .meeting-print-only { display: none !important; }
}
@media print {
  @page { margin: 14mm 16mm; size: A4; }
  html, body, #root {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    background: #fff !important;
    color: #14201d !important;
  }
  body * { visibility: hidden !important; }
  #meeting-summary-print,
  #meeting-summary-print * { visibility: visible !important; }
  #meeting-summary-print {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    padding: 0 2mm !important;
    margin: 0 !important;
    background: #fff !important;
    overflow: visible !important;
    height: auto !important;
    max-height: none !important;
    box-shadow: none !important;
    border: none !important;
    font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif !important;
    color: #14201d !important;
    line-height: 1.45 !important;
  }
  #meeting-summary-print,
  #meeting-summary-print * {
    overflow: visible !important;
    max-height: none !important;
    box-shadow: none !important;
  }
  .meeting-print-actions,
  .meeting-no-print,
  .meeting-screen-only {
    display: none !important;
    visibility: hidden !important;
    height: 0 !important;
    overflow: hidden !important;
  }
  .meeting-print-only {
    display: block !important;
    visibility: visible !important;
  }
  .mp-brand {
    display: flex !important;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 10px;
    margin-bottom: 14px;
    border-bottom: 3px solid #00c2a3;
  }
  .mp-brand-mark {
    font-size: 11px !important;
    font-weight: 800 !important;
    letter-spacing: 0.14em !important;
    text-transform: uppercase !important;
    color: #00c2a3 !important;
  }
  .mp-brand-sub {
    font-size: 10px !important;
    color: #5a6b66 !important;
  }
  .mp-title {
    font-size: 20px !important;
    font-weight: 800 !important;
    line-height: 1.25 !important;
    margin: 0 0 10px !important;
    color: #0f1f1b !important;
  }
  .mp-meta {
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    gap: 4px 16px;
    padding: 10px 12px;
    margin: 0 0 14px !important;
    background: #f4faf8 !important;
    border: 1px solid #d7e8e3 !important;
    border-radius: 8px !important;
    font-size: 11px !important;
    color: #3d524c !important;
  }
  .mp-meta-label {
    font-weight: 700 !important;
    color: #067a68 !important;
    margin-right: 4px;
  }
  .mp-raw {
    font-size: 12px !important;
    line-height: 1.5 !important;
    margin: 0 0 16px !important;
    padding: 10px 12px;
    border-left: 3px solid #00c2a3;
    background: #fafcfb !important;
    white-space: pre-wrap !important;
  }
  .mp-section, .meeting-print-section {
    break-inside: avoid;
    page-break-inside: avoid;
    margin: 0 0 16px !important;
  }
  .mp-section-title {
    display: flex !important;
    align-items: center;
    gap: 8px;
    font-size: 12px !important;
    font-weight: 800 !important;
    letter-spacing: 0.04em !important;
    text-transform: uppercase !important;
    color: #067a68 !important;
    margin: 0 0 8px !important;
    padding-bottom: 4px;
    border-bottom: 1px solid #cfe4de !important;
  }
  .mp-section-count {
    display: inline-block !important;
    min-width: 18px;
    padding: 1px 6px;
    border-radius: 999px;
    background: #00c2a3 !important;
    color: #fff !important;
    font-size: 10px !important;
    font-weight: 800 !important;
    text-align: center;
  }
  .mp-item {
    display: grid !important;
    grid-template-columns: 22px 1fr;
    gap: 6px;
    margin: 0 0 8px !important;
    font-size: 12px !important;
    line-height: 1.45 !important;
  }
  .mp-item-num {
    font-weight: 800 !important;
    color: #00c2a3 !important;
    font-size: 11px !important;
  }
  .mp-item-text {
    white-space: pre-wrap !important;
    color: #14201d !important;
  }
  .mp-item-refs { display: block !important; margin-top: 3px; }
  .mp-ref-chip {
    display: inline-block !important;
    margin: 0 4px 2px 0;
    padding: 1px 6px;
    border-radius: 4px;
    background: #e8f6f4 !important;
    border: 1px solid #b7e0d8 !important;
    color: #067a68 !important;
    font-weight: 700 !important;
    font-size: 10px !important;
  }
  .mp-thumbs {
    display: flex !important;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }
  .mp-thumb {
    width: 56px !important;
    height: 56px !important;
    border-radius: 6px !important;
    overflow: hidden !important;
    border: 1px solid #cfe4de !important;
    background: #111 !important;
    position: relative !important;
    flex: 0 0 auto;
  }
  .mp-thumb img,
  .mp-thumb video {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    display: block !important;
  }
  .mp-thumb-label {
    position: absolute !important;
    left: 3px;
    bottom: 3px;
    padding: 0 4px;
    border-radius: 3px;
    background: rgba(0,0,0,0.72) !important;
    color: #fff !important;
    font-size: 8px !important;
    font-weight: 800 !important;
    line-height: 1.4 !important;
  }
  .mp-transcript {
    font-size: 10px !important;
    line-height: 1.4 !important;
    color: #4f6861 !important;
    white-space: pre-wrap !important;
    columns: 2;
    column-gap: 16px;
  }
  .mp-footer {
    margin-top: 18px;
    padding-top: 8px;
    border-top: 1px solid #d7e8e3 !important;
    font-size: 9px !important;
    color: #6a8079 !important;
    display: flex !important;
    justify-content: space-between;
  }
}
`;
  document.head.appendChild(style);
}

/** Dispara el diálogo de impresión del resumen de reunión. */
export function printMeetingSummary() {
  ensurePrintStyles();
  const videos = document.querySelectorAll(
    "#meeting-summary-print .mp-thumb video",
  );
  videos.forEach((v) => {
    try {
      v.load?.();
    } catch {
      /* ignore */
    }
  });
  window.setTimeout(() => window.print(), videos.length ? 280 : 50);
}

function itemPhotoIds(item) {
  if (Array.isArray(item?.photoIds) && item.photoIds.length) {
    return item.photoIds.map(Number).filter((n) => Number.isFinite(n));
  }
  if (item?.photoId != null) {
    const n = Number(item.photoId);
    return Number.isFinite(n) ? [n] : [];
  }
  return [];
}

function FlatSection({ title, items, photosById }) {
  if (!items?.length) return null;
  return (
    <Box className="mp-section meeting-print-section">
      <Typography component="h2" className="mp-section-title">
        {title}
        <Box component="span" className="mp-section-count">
          {items.length}
        </Box>
      </Typography>
      {items.map((item, idx) => {
        const ids = itemPhotoIds(item);
        const text = typeof item === "string" ? item : item?.text;
        if (!String(text || "").trim()) return null;
        return (
          <Box key={`${title}-${idx}`} className="mp-item">
            <Box component="span" className="mp-item-num">
              {idx + 1}.
            </Box>
            <Box>
              <Typography component="div" className="mp-item-text">
                {text}
              </Typography>
              {ids.length ? (
                <Box className="mp-thumbs">
                  {ids.map((id) => {
                    const photo = photosById?.get(Number(id));
                    if (!photo?.fileUrl) {
                      return (
                        <Box key={id} className="mp-thumb">
                          <Box className="mp-thumb-label">#{id}</Box>
                        </Box>
                      );
                    }
                    const isVideo =
                      photo.kind === "video" ||
                      mediaKindFromMime(photo.mimeType) === "video";
                    const src = meetingMediaUrl(photo.fileUrl);
                    return (
                      <Box key={id} className="mp-thumb">
                        {isVideo ? (
                          <Box
                            component="video"
                            src={`${src}${src.includes("#") ? "" : "#t=0.1"}`}
                            muted
                            playsInline
                            preload="metadata"
                          />
                        ) : (
                          <Box component="img" src={src} alt={`#${id}`} />
                        )}
                        <Box className="mp-thumb-label">
                          {isVideo ? "V" : "F"}#{id}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function PrintSection({
  title,
  items,
  photosById,
  defaultExpanded = true,
  onOpenMedia,
}) {
  if (!items?.length) return null;
  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={defaultExpanded}
      sx={{
        mb: 1,
        bgcolor: "transparent",
        "&:before": { display: "none" },
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
      className="meeting-print-section meeting-screen-only"
    >
      <AccordionSummary
        expandIcon={<ExpandMoreOutlinedIcon />}
        sx={{ px: 0, minHeight: 44 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title} ({items.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 1.5 }}>
        <Stack spacing={1.25}>
          {items.map((item, idx) => {
            const ids = itemPhotoIds(item);
            return (
              <Box key={`${title}-${idx}`}>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {item.text}
                </Typography>
                {ids.length ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 0.75 }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {ids.map((pid) => {
                      const photo = photosById.get(Number(pid));
                      if (!photo) {
                        return (
                          <Typography
                            key={pid}
                            variant="caption"
                            color="text.secondary"
                          >
                            #{pid}
                          </Typography>
                        );
                      }
                      const isVideo =
                        photo.kind === "video" ||
                        mediaKindFromMime(photo.mimeType) === "video";
                      const open = () => onOpenMedia?.(Number(pid));
                      return (
                        <Box
                          key={pid}
                          component={onOpenMedia ? "button" : "div"}
                          type={onOpenMedia ? "button" : undefined}
                          onClick={onOpenMedia ? open : undefined}
                          sx={{
                            width: 80,
                            p: 0,
                            m: 0,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.25,
                            bgcolor: "background.paper",
                            cursor: onOpenMedia ? "pointer" : "default",
                            textAlign: "left",
                            overflow: "hidden",
                          }}
                        >
                          {isVideo ? (
                            <Box
                              sx={{
                                width: "100%",
                                height: 72,
                                bgcolor: "grey.900",
                                display: "grid",
                                placeItems: "center",
                                color: "common.white",
                              }}
                              aria-hidden
                            >
                              ▶
                            </Box>
                          ) : (
                            <Box
                              component="img"
                              src={meetingMediaUrl(photo.fileUrl)}
                              alt=""
                              loading="lazy"
                              sx={{
                                width: "100%",
                                height: 72,
                                objectFit: "cover",
                                display: "block",
                                pointerEvents: "none",
                              }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            color="primary"
                            display="block"
                            sx={{
                              px: 0.75,
                              py: 0.35,
                              fontWeight: 800,
                              lineHeight: 1.2,
                            }}
                          >
                            {isVideo ? "Vídeo" : "Foto"} #{pid}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

/** Bloque imprimible del resumen. Solo lectura: no toca media.
 * compact=true: en pantalla solo blurb + PDF (el detalle vive en el feed).
 */
export default function MeetingSummaryPrint({
  session,
  summary,
  onOpenMedia,
  compact = false,
  summaryLang = "es",
  onSummaryLangChange,
  hasSummaryEn = false,
  hideActions = false,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    ensurePrintStyles();
  }, []);

  const photos = session?.photos || [];
  const photosById = useMemo(
    () => new Map(photos.map((p) => [Number(p.id), p])),
    [photos],
  );

  const transcript = String(
    summary?.transcript ||
      summary?.finalTranscript ||
      summary?.fullTranscript ||
      "",
  ).trim();

  const visualNotes = useMemo(() => {
    const notes = Array.isArray(summary?.visualNotes)
      ? summary.visualNotes
      : [];
    const videoish = [];
    const photoish = [];
    for (const n of notes) {
      const text = String(n?.text || "");
      const ids = itemPhotoIds(n);
      const linkedVideo = ids.some((id) => {
        const p = photosById.get(id);
        return (
          p && (p.kind === "video" || mediaKindFromMime(p.mimeType) === "video")
        );
      });
      if (linkedVideo || /^vídeo\s*#/i.test(text) || /\bvídeo\b/i.test(text)) {
        videoish.push(n);
      } else {
        photoish.push(n);
      }
    }
    return { videoish, photoish };
  }, [summary?.visualNotes, photosById]);

  const blurb = useMemo(
    () => shortMeetingBlurb(summary?.rawSummary, summary?.headline),
    [summary?.rawSummary, summary?.headline],
  );

  const fromVideos = useMemo(() => {
    if (Array.isArray(summary?.fromVideos) && summary.fromVideos.length) {
      return summary.fromVideos;
    }
    return extractVideoTranscriptSections(transcript);
  }, [summary?.fromVideos, transcript]);

  const mediaNotesForPrint = useMemo(() => {
    const map = notesByPhotoId(summary, transcript, { photos });
    const audioMap = audioCouplingByPhotoId(photos, session?.audioChunks || []);
    const items = [];
    for (const photo of photos) {
      const bucket = map.get(Number(photo.id));
      const audioLink = audioMap.get(Number(photo.id));
      const isVideo =
        photo.kind === "video" || mediaKindFromMime(photo.mimeType) === "video";
      const parts = [
        ...(bucket?.spoken || []),
        ...(!isVideo ? audioLink?.spoken || [] : []),
        ...(bucket?.linked || []),
        ...(isVideo ? bucket?.visual || [] : []),
      ].filter(Boolean);
      // dedupe
      const unique = [];
      for (const p of parts) {
        if (p && !unique.includes(p)) unique.push(p);
      }
      const teamNote = String(photo.teamNote || "").trim();
      if (!unique.length && !teamNote) continue;
      const audioLabel =
        !isVideo && audioLink?.chunk?.seq != null
          ? ` · audio ${Number(audioLink.chunk.seq) + 1}`
          : "";
      const bodyParts = [];
      if (unique.length) bodyParts.push(unique.join(" · "));
      if (teamNote) bodyParts.push(`Nota STAGE: ${teamNote}`);
      items.push({
        text: `${isVideo ? "Vídeo" : "Foto"} #${photo.id}${
          photo.offsetLabel ? ` · ${photo.offsetLabel}` : ""
        }${audioLabel}: ${bodyParts.join(" · ")}`,
        photoIds: [Number(photo.id)],
      });
    }
    return items;
  }, [summary, transcript, photos, session?.audioChunks]);

  if (!summary || summary.status === "error") return null;

  const photoishNotes = visualNotes.photoish.length
    ? visualNotes.photoish
    : summary.visualNotes;

  return (
    <Box id="meeting-summary-print" className="meeting-summary-print-root">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1.25}
        sx={{ mb: 1.5 }}
        className="meeting-print-actions meeting-screen-only"
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, wordBreak: "break-word", lineHeight: 1.3 }}
        >
          {summary.headline || t("Meetings.summary")}
        </Typography>
        {!hideActions ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {hasSummaryEn || typeof onSummaryLangChange === "function" ? (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={summaryLang}
                onChange={(_, next) => {
                  if (!next || typeof onSummaryLangChange !== "function")
                    return;
                  onSummaryLangChange(next);
                }}
                className="meeting-no-print"
              >
                <ToggleButton value="es" aria-label="ES">
                  ES
                </ToggleButton>
                <ToggleButton
                  value="en"
                  aria-label="EN"
                  disabled={!hasSummaryEn}
                >
                  EN
                </ToggleButton>
              </ToggleButtonGroup>
            ) : null}
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintOutlinedIcon />}
              onClick={printMeetingSummary}
              sx={{
                alignSelf: { xs: "stretch", sm: "center" },
                minHeight: 40,
              }}
            >
              {t("Meetings.printExport")}
            </Button>
          </Stack>
        ) : null}
      </Stack>

      <Box className="meeting-print-meta meeting-screen-only" sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.45, wordBreak: "break-word" }}
        >
          {[
            session?.title,
            session?.customerName
              ? `${t("Meetings.customer")}: ${session.customerName}`
              : null,
            session?.meetingDate
              ? `${t("Meetings.date")}: ${session.meetingDate}`
              : null,
            session?.attendeeNames
              ? `${t("Meetings.attendees")}: ${session.attendeeNames}`
              : null,
            session?.clientContactNames
              ? `${t("Meetings.clientAttendees")}: ${session.clientContactNames}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {t("Meetings.counts", {
            audio: session?.audioCount || 0,
            photos: session?.imageCount ?? session?.photoCount ?? 0,
            videos: session?.videoCount || 0,
          })}
        </Typography>
      </Box>

      {blurb ? (
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            whiteSpace: "pre-wrap",
            lineHeight: 1.5,
            maxWidth: 72 * 8,
          }}
          className="meeting-print-raw meeting-screen-only"
        >
          {blurb}
        </Typography>
      ) : null}

      {/* Pantalla: acordeones (en compact, sin notas de media — están en el feed) */}
      <Box className="meeting-screen-only">
        {!compact ? (
          <>
            <PrintSection
              title={t("Meetings.fromVideos")}
              items={fromVideos}
              photosById={photosById}
              onOpenMedia={onOpenMedia}
            />
            <PrintSection
              title={t("Meetings.videoVisualNotes")}
              items={visualNotes.videoish}
              photosById={photosById}
              onOpenMedia={onOpenMedia}
            />
            <PrintSection
              title={t("Meetings.visualNotes")}
              items={photoishNotes}
              photosById={photosById}
              onOpenMedia={onOpenMedia}
              defaultExpanded={!visualNotes.videoish.length}
            />
            <PrintSection
              title={t("Meetings.forDesign")}
              items={summary.forDesign}
              photosById={photosById}
              onOpenMedia={onOpenMedia}
            />
            <PrintSection
              title={t("Meetings.forSales")}
              items={summary.forSales}
              photosById={photosById}
              onOpenMedia={onOpenMedia}
            />
            <PrintSection
              title={t("Meetings.decisions")}
              items={summary.decisions}
              photosById={photosById}
              onOpenMedia={onOpenMedia}
            />
            <PrintSection
              title={t("Meetings.openQuestions")}
              items={summary.openQuestions}
              photosById={photosById}
              onOpenMedia={onOpenMedia}
            />
          </>
        ) : null}
      </Box>

      {/* Impresión: documento maquetado */}
      <Box className="meeting-print-only mp-doc">
        <Box className="mp-brand">
          <Box component="span" className="mp-brand-mark">
            STAGE · Acta de reunión
          </Box>
          <Box component="span" className="mp-brand-sub">
            {session?.meetingDate || ""}
          </Box>
        </Box>

        <Typography component="h1" className="mp-title">
          {summary.headline || session?.title || t("Meetings.summary")}
        </Typography>

        <Box className="mp-meta">
          {session?.customerName ? (
            <Box className="mp-meta-row">
              <Box component="span" className="mp-meta-label">
                {t("Meetings.customer")}:
              </Box>
              {session.customerName}
            </Box>
          ) : null}
          {session?.meetingDate ? (
            <Box className="mp-meta-row">
              <Box component="span" className="mp-meta-label">
                {t("Meetings.date")}:
              </Box>
              {session.meetingDate}
            </Box>
          ) : null}
          {session?.attendeeNames ? (
            <Box className="mp-meta-row">
              <Box component="span" className="mp-meta-label">
                {t("Meetings.attendees")}:
              </Box>
              {session.attendeeNames}
            </Box>
          ) : null}
          {session?.clientContactNames ? (
            <Box className="mp-meta-row">
              <Box component="span" className="mp-meta-label">
                {t("Meetings.clientAttendees")}:
              </Box>
              {session.clientContactNames}
            </Box>
          ) : null}
          <Box className="mp-meta-row">
            <Box component="span" className="mp-meta-label">
              Media:
            </Box>
            {t("Meetings.counts", {
              audio: session?.audioCount || 0,
              photos: session?.imageCount ?? session?.photoCount ?? 0,
              videos: session?.videoCount || 0,
            })}
          </Box>
        </Box>

        {blurb ? (
          <Typography component="div" className="mp-raw">
            {blurb}
          </Typography>
        ) : null}

        <FlatSection
          title={t("Meetings.forDesign")}
          items={summary.forDesign}
          photosById={photosById}
        />
        <FlatSection
          title={t("Meetings.forSales")}
          items={summary.forSales}
          photosById={photosById}
        />
        <FlatSection
          title={t("Meetings.decisions")}
          items={summary.decisions}
          photosById={photosById}
        />
        <FlatSection
          title={t("Meetings.openQuestions")}
          items={summary.openQuestions}
          photosById={photosById}
        />
        <FlatSection
          title={t("Meetings.mediaNotesPrint")}
          items={mediaNotesForPrint}
          photosById={photosById}
        />
        <FlatSection
          title={t("Meetings.fromVideos")}
          items={fromVideos}
          photosById={photosById}
        />

        <Box className="mp-footer">
          <span>STAGE Design · CRM Reuniones</span>
          <span>
            {[session?.title, summary.headline].filter(Boolean).join(" · ")}
          </span>
        </Box>
      </Box>
    </Box>
  );
}
